import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AlertCircle, Calendar, Trash2, Loader2, UserPlus, Plus, X, Pencil } from 'lucide-react'

interface ClientUser {
  uuid: string
  username: string
  email: string
  created_at: string
  must_change_password: boolean
}

function errorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    fallback
  )
}

export default function UsersPage() {
  const { recruiter } = useAuth()
  const [users, setUsers] = useState<ClientUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userToDelete, setUserToDelete] = useState<ClientUser | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)

  const [showFormDialog, setShowFormDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const fetchUsers = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await authApi.listUsers()
      if (response.status === 'success') {
        setUsers(response.users)
      } else {
        setError('שגיאה בטעינת המשתמשים')
      }
    } catch (err: unknown) {
      setError(errorMessage(err, 'שגיאה בטעינת המשתמשים'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [recruiter])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const openCreate = () => {
    setEditingUser(null)
    setUsername('')
    setEmail('')
    setPassword('')
    setShowFormDialog(true)
  }

  const openEdit = (user: ClientUser) => {
    setEditingUser(user)
    setUsername(user.username)
    setEmail(user.email)
    setPassword('')
    setShowFormDialog(true)
  }

  const closeForm = () => {
    setShowFormDialog(false)
    setEditingUser(null)
    setUsername('')
    setEmail('')
    setPassword('')
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username.trim() || !email.trim()) {
      setError('נא למלא שם משתמש ואימייל')
      return
    }
    if (!editingUser && password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים')
      return
    }
    if (password && password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים')
      return
    }
    try {
      setIsSaving(true)
      setError(null)
      if (editingUser) {
        await authApi.updateUser(editingUser.uuid, username.trim(), email.trim(), password)
      } else {
        await authApi.createUser(username.trim(), email.trim(), password)
      }
      closeForm()
      await fetchUsers()
    } catch (err: unknown) {
      setError(errorMessage(err, editingUser ? 'שגיאה בעדכון המשתמש' : 'שגיאה ביצירת המשתמש'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return
    try {
      setIsDeleting(true)
      setError(null)
      await authApi.deleteUser(userToDelete.uuid)
      setUserToDelete(null)
      await fetchUsers()
    } catch (err: unknown) {
      setError(errorMessage(err, 'שגיאה במחיקת המשתמש'))
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="bg-background p-6 flex flex-col min-h-[calc(100vh-96px)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">משתמשים</h1>
          <p className="text-muted-foreground mt-1">
            חשבונות מגייסים. תוצאות המבחנים נשלחות לאימייל של המשתמש ששלח את המבחן.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{users.length} משתמשים</span>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50 mt-4">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex-1 mt-4 min-h-0">
        {users.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">אין משתמשים</p>
                <p className="text-sm text-muted-foreground mt-2">
                  לחץ על &quot;הוסף משתמש&quot; למטה כדי ליצור חשבון מגייס.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="flex flex-col max-h-full overflow-hidden">
            <CardHeader>
              <CardTitle>רשימת משתמשים</CardTitle>
            </CardHeader>
            <CardContent className="min-h-0 overflow-auto">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-right p-4 font-semibold">שם משתמש</th>
                      <th className="text-right p-4 font-semibold">אימייל</th>
                      <th className="text-right p-4 font-semibold">סטטוס</th>
                      <th className="text-right p-4 font-semibold">תאריך הוספה</th>
                      <th className="text-right p-4 font-semibold w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.uuid}
                        className="border-b hover:bg-muted/50 transition-colors"
                        onMouseEnter={() => setHoveredRowId(user.uuid)}
                        onMouseLeave={() => setHoveredRowId(null)}
                      >
                        <td className="p-4 font-medium">{user.username}</td>
                        <td className="p-4 text-muted-foreground">{user.email}</td>
                        <td className="p-4">
                          {user.must_change_password ? 'ממתין לסיסמה' : 'פעיל'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{formatDate(user.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-4 w-24">
                          <div
                            className={`flex items-center gap-1 transition-opacity duration-200 ${
                              hoveredRowId === user.uuid ? 'opacity-100 visible' : 'opacity-0 invisible'
                            }`}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              onClick={() => openEdit(user)}
                              title="ערוך משתמש"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              onClick={() => setUserToDelete(user)}
                              title="מחק משתמש"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          הוסף משתמש
        </Button>
      </div>

      {showFormDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingUser ? 'עריכת משתמש' : 'הוסף משתמש'}</CardTitle>
                <Button variant="ghost" size="icon" onClick={closeForm} disabled={isSaving}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">שם משתמש</label>
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="שם משתמש"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">אימייל</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {editingUser ? 'סיסמה זמנית חדשה' : 'סיסמה זמנית'}
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={editingUser ? 'השאר ריק כדי לא לשנות' : 'לפחות 6 תווים'}
                    disabled={isSaving}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={closeForm} disabled={isSaving}>
                    ביטול
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving || !username.trim() || !email.trim() || (!editingUser && password.length < 6)}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        שומר...
                      </>
                    ) : editingUser ? (
                      'עדכן משתמש'
                    ) : (
                      'הוסף משתמש'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>מחיקת משתמש</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                האם אתה בטוח שברצונך למחוק את המשתמש <strong>{userToDelete.username}</strong> ({userToDelete.email})?
              </p>
              <p className="text-xs text-muted-foreground">פעולה זו לא ניתנת לביטול.</p>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setUserToDelete(null)} disabled={isDeleting}>
                  ביטול
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      מוחק...
                    </>
                  ) : (
                    'מחק'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
